import _ from 'lodash';
import {
  isNullOrEmpty,
  isNullOrUndefinedOrEmptyStr,
} from '../../utilities/stringUtil';
import {
  WORKER_IMAGE_REGISTER_STATUS,
  WORKER_IMAGE_CCUS_SYNCED_STATUS,
  SORT_TYPE,
  SPOT_WORKER_STATUS,
  SPOT_WORKER_RESIDENCE_TYPE,
  SPOT_WORKER_TYPE,
  FACE_PICTURE_UPLOAD_STATUS,
  DELETE_FILE_FLAG,
  ERROR_MESSAGE,
} from '../../utilities/constants';
import Joi from 'joi-browser';
import regex from '../../utilities/validateRegex';

/**
 * 検索用パラメータを作成
 * @param {object} param 検索パラメーター
 * @param {object} residenceType SpotWorker区別(常駐、非常駐)
 * @return {object}searchParam:API通信用検索パラメーター
 * */
const createSearchSpotWorkerList = (param, residenceType) => {
  // そのままではlengthが取れないため一旦変数に代入する
  const workerImageRegisterStatus = WORKER_IMAGE_REGISTER_STATUS;
  const workerImageCcusSyncedStatus = _.filter(
    WORKER_IMAGE_CCUS_SYNCED_STATUS,
    'canSearch'
  );
  const spotWorkerStatus = SPOT_WORKER_STATUS;
  const spotWorkerType = _.filter(SPOT_WORKER_TYPE, {
    residenceTypeId: residenceType.id,
  });

  const searchParam = Object.create(null);
  searchParam.offset = param.offset;
  searchParam.limit = param.limit;

  if (!isNullOrEmpty(param.spotWorkerName)) {
    searchParam.spot_worker_name = String(param.spotWorkerName);
  }

  if (!isNullOrEmpty(param.companyName)) {
    searchParam.company_name = String(param.companyName);
  }

  // 『workerImage』は現場設定ページで使用している
  // 現場設定ページのプロパティ名を変更できる機会があれば変更する
  if (
    !isNullOrEmpty(param.workerImageRegisterStatusList) &&
    param.workerImageRegisterStatusList.length > 0 &&
    param.workerImageRegisterStatusList.length !==
      Object.keys(workerImageRegisterStatus).length
  ) {
    searchParam.worker_image_register_status = param.workerImageRegisterStatusList.map(
      item => item.id
    );
  }

  // 『registerStatus』は現場設定ページで使用している
  // 現場設定ページのプロパティ名を変更できる機会があれば変更する
  if (
    !isNullOrEmpty(param.workerImageCcusSyncedStatusList) &&
    param.workerImageCcusSyncedStatusList.length > 0 &&
    param.workerImageCcusSyncedStatusList.length !==
      workerImageCcusSyncedStatus.length
  ) {
    searchParam.worker_image_synced_status = param.workerImageCcusSyncedStatusList.map(
      registerStatus => registerStatus.id
    );
  }

  // 『hasError』は現場設定ページで使用している
  // 現場設定ページのプロパティ名を変更できる機会があれば変更する
  if (!isNullOrEmpty(param.hasErrorList) && param.hasErrorList.length > 0) {
    searchParam.worker_image_has_error = Number(param.hasErrorList[0].id);
  } else if (!isNullOrEmpty(param.hasError)) {
    searchParam.worker_image_has_error = Number(param.hasError);
  }

  if (
    !isNullOrEmpty(param.statusList) &&
    param.statusList.length > 0 &&
    param.statusList.length !== Object.keys(spotWorkerStatus).length
  ) {
    searchParam.spot_worker_status = param.statusList.map(status => status.id);
  }

  if (!isNullOrEmpty(param.workerType) && param.workerType.length > 0) {
    searchParam.worker_type = param.workerType.map(item => item.id);
  } else {
    searchParam.worker_type = spotWorkerType.map(item => item.id);
  }

  if (!isNullOrEmpty(param.order)) {
    if (param.sortType === SORT_TYPE.asc.id) {
      searchParam.order = param.order + SORT_TYPE.asc.name;
    } else {
      searchParam.order = param.order + SORT_TYPE.desc.name;
    }
  }

  return searchParam;
};

/**
 * バリデーション用のスキーマを作成する
 * @param {object} spotWorkerResidentType SpotWorker区別(常駐、非常駐)
 * @return {object} validationSchema:バリデーション用のスキーマ
 * */
const createValidationSchema = spotWorkerResidentType => {
  const validateSchema = Object.create(null);
  validateSchema.worker_type = Joi.number().required();
  validateSchema.company_name = Joi.string()
    .trim()
    .max(255)
    .allow('');
  validateSchema.name_sei = Joi.string()
    .trim()
    .required()
    .max(255);
  validateSchema.name_mei = Joi.string()
    .trim()
    .required()
    .max(255);
  validateSchema.name_sei_kana = Joi.string()
    .required()
    .max(255);
  validateSchema.name_mei_kana = Joi.string()
    .required()
    .max(255);

  switch (spotWorkerResidentType.id) {
    case SPOT_WORKER_RESIDENCE_TYPE.RESIDENT.id:
      validateSchema.name_sei_kana = validateSchema.name_sei_kana
        .label('hiragana')
        .regex(regex.pattern.hiragana);
      validateSchema.name_mei_kana = validateSchema.name_mei_kana
        .label('hiragana')
        .regex(regex.pattern.hiragana);
      break;
    case SPOT_WORKER_RESIDENCE_TYPE.NON_RESIDENT.id:
      validateSchema.name_sei_kana = validateSchema.name_sei_kana
        .label('katakana')
        .regex(regex.pattern.katakana, { name: 'katakana' });
      validateSchema.name_mei_kana = validateSchema.name_mei_kana
        .label('katakana')
        .regex(regex.pattern.katakana, { name: 'katakana' });
      break;
  }

  return validateSchema;
};

/**
 * 登録・更新用パラメータを作成
 * @param {object}param:登録パラメーター
 * @return {object}registerParam:API通信用登録パラメーター
 * */
const createRegisterUpdateParam = param => {
  const registerParam = Object.create(null);
  if (!isNullOrUndefinedOrEmptyStr(param.name_sei)) {
    registerParam.name_sei = param.name_sei;
  }
  if (!isNullOrUndefinedOrEmptyStr(param.name_mei)) {
    registerParam.name_mei = param.name_mei;
  }
  if (!isNullOrUndefinedOrEmptyStr(param.name_sei_kana)) {
    registerParam.name_sei_kana = param.name_sei_kana;
  }
  if (!isNullOrUndefinedOrEmptyStr(param.name_mei_kana)) {
    registerParam.name_mei_kana = param.name_mei_kana;
  }
  if (!isNullOrUndefinedOrEmptyStr(param.worker_type)) {
    registerParam.worker_type = `${param.worker_type}`;
  }
  if (!isNullOrUndefinedOrEmptyStr(param.company_name)) {
    registerParam.spot_company_name = param.company_name;
  }
  return registerParam;
};

const createUpdateFacePicture = param => {
  let formData = new FormData();
  if (!isNullOrEmpty(param.fieldSpotWorkerId)) {
    formData.append('field_spot_worker_id', param.fieldSpotWorkerId);
  }
  if (!isNullOrEmpty(param.imageBinary)) {
    formData.append('image', param.imageBinary);
  }
  if (param.uploadStatus === FACE_PICTURE_UPLOAD_STATUS.deleteFile.id) {
    formData.append('delete_flg', DELETE_FILE_FLAG.deleteFlag.id);
  } else {
    formData.append('delete_flg', DELETE_FILE_FLAG.notDeleteFlag.id);
  }

  return formData;
};

/**
 * 現場常駐社員・非常駐入退場者写真の処理
 * @param {*} worker 現場常駐社員・非常駐入退場者情報
 */
const processImages = worker => {
  let allCcusSyncedStatuses = worker.spot_worker_images.map(image => {
    const matching_status = _.find(WORKER_IMAGE_CCUS_SYNCED_STATUS, {
      id: image.synced_status,
    });
    let formatted = `${image.integration_service_name}：${
      matching_status.name
    }`;
    return {
      integration_service_name: image.integration_service_name,
      ccus_synced_status: image.synced_status,
      formatted,
    };
  });

  let allErrors = worker.spot_worker_images.map(image => {
    if (image.has_error === false) {
      image.error_message = 'ー';
    }
    let formatted = `${image.integration_service_name}：${image.error_message}`;
    return {
      integration_service_name: image.integration_service_name,
      has_error: image.has_error,
      error_message: image.error_message,
      formatted,
    };
  });

  let ccusSyncedStatus =
    worker.worker_image_register_status === 1
      ? WORKER_IMAGE_CCUS_SYNCED_STATUS.notRegister.id
      : WORKER_IMAGE_CCUS_SYNCED_STATUS.excluded.id;
  if (allCcusSyncedStatuses.length > 0) {
    const allCcusSyncedStatusesSame =
      _.uniqBy(allCcusSyncedStatuses, item => item.ccus_synced_status).length ==
      1;
    if (allCcusSyncedStatusesSame) {
      ccusSyncedStatus = allCcusSyncedStatuses[0].ccus_synced_status;
    } else {
      ccusSyncedStatus = WORKER_IMAGE_CCUS_SYNCED_STATUS.someRegister.id;
    }
  }
  if (isNullOrEmpty(worker.worker_image_exclusion_reason) === false) {
    ccusSyncedStatus = WORKER_IMAGE_CCUS_SYNCED_STATUS.excluded.id;
  }

  let hasError = allErrors.some(item => item.has_error);

  let errorMessage = 'ー';
  if (hasError) {
    const allErrorMessagesSame =
      _.uniqBy(allErrors, item => item.error_message).length == 1;
    if (allErrorMessagesSame) {
      errorMessage = allErrors[0].error_message;
    } else {
      const someWithoutError = allErrors.some(item => !item.has_error);
      if (someWithoutError) {
        errorMessage = ERROR_MESSAGE.someErrors;
      } else {
        errorMessage = ERROR_MESSAGE.allErrors;
      }
    }
  }

  worker.worker_image_info = {
    allCcusSyncedStatuses,
    allErrors,
    ccusSyncedStatus,
    hasError,
    errorMessage,
  };
};

/**
 * 現場常駐社員・非常駐入退場者一覧画面で利用するビジネスロジック格納クラス
 * */
const SpotWorkersService = {
  createSearchSpotWorkerList,
  createValidationSchema,
  createRegisterUpdateParam,
  createUpdateFacePicture,
  processImages,
};

export default SpotWorkersService;



// WEBPACK FOOTER //
// ./src/services/spotWorkers/SpotWorkersService.js